import React, { useState, useMemo } from 'react';
import './OfferModal.css';
import { haptic } from '../../utils/haptic';

interface Product {
  id: string;
  name: string;
  images: string[];
  price?: number;
  salePrice?: number;
  topOffer?: number | null;
}

interface OfferModalProps {
  product: Product;
  onClose: () => void;
  /** Returns a Promise so the modal can show a loading spinner and
   *  surface server-side rejections (validation, race-condition) to
   *  the user. The pre-2026-05-17 fire-and-forget signature meant the
   *  modal closed before the API even responded. */
  onSubmit: (amount: number, message: string) => void | Promise<boolean | void>;
}

const OfferModal: React.FC<OfferModalProps> = ({ product, onClose, onSubmit }) => {
  const askingPrice = product.salePrice || product.price || 0;
  const currentTop = product.topOffer ?? null;

  // Suggested minimum: $5 above current top offer if there is one,
  // otherwise 80% of asking price (typical fair-offer floor).
  const suggestedMin = useMemo(() => {
    if (currentTop && currentTop > 0) return Math.ceil(currentTop + 5);
    if (askingPrice > 0) return Math.max(10, Math.ceil(askingPrice * 0.8));
    return 10;
  }, [currentTop, askingPrice]);

  const [amount, setAmount] = useState<string>(String(suggestedMin));
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const offerAmount = parseFloat(amount) || 0;
  const isOutbid = currentTop !== null && offerAmount <= currentTop;
  const isBelowMin = offerAmount < 10;
  const isLeading = currentTop === null || offerAmount > currentTop;

  const handleQuickBid = (delta: number) => {
    const base = currentTop ?? suggestedMin;
    setAmount(String(Math.ceil(base + delta)));
    setError('');
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (isBelowMin) {
      setError('Minimum offer is $10');
      return;
    }
    if (currentTop !== null && offerAmount <= currentTop) {
      setError(`Your bid needs to beat the current top offer of $${currentTop}. Try at least $${currentTop + 1}.`);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const result = await onSubmit(offerAmount, message);
      // If parent returns explicit false, treat as failure.
      if (result === false) {
        setError('Offer failed — please try again.');
        setSubmitting(false);
        return;
      }
      haptic('success');
      setSubmitted(true);
      setTimeout(onClose, 1400);
    } catch (e: any) {
      haptic('error');
      setError(e?.message || 'Offer failed — please try again.');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="offer-modal offer-modal--success animate-slide-up" onClick={e => e.stopPropagation()}>
          <div className="modal-handle" />
          <div className="offer-success">
            <div className="offer-success-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
            <h3>Offer Sent</h3>
            <p>Your ${offerAmount} offer is in.</p>
            <p className="offer-success-sub">Jalen will respond via DM — usually within 24 hours.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="offer-modal animate-slide-up" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" />

        <div className="offer-header">
          <img src={product.images[0]} alt={product.name} className="offer-product-img" />
          <div className="offer-product-info">
            <h3>Make an Offer</h3>
            <p>{product.name}</p>
          </div>
        </div>

        {/* Context bar — asking price + current top + leading indicator */}
        <div className="offer-context-bar">
          {askingPrice > 0 && (
            <div className="offer-context-item">
              <span className="offer-context-label">Starting bid</span>
              <span className="offer-context-value">${askingPrice}</span>
            </div>
          )}
          {currentTop !== null && (
            <div className="offer-context-item offer-context-item--top">
              <span className="offer-context-label">Top offer</span>
              <span className="offer-context-value">${currentTop}</span>
            </div>
          )}
        </div>

        <div className="offer-form">
          <label className="offer-label">Your Offer</label>
          <div className="offer-input-wrapper">
            <span className="currency-symbol">$</span>
            <input
              type="number"
              placeholder="Enter amount"
              value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}
              className="offer-input"
              min="10"
              step="1"
              disabled={submitting}
              autoFocus
            />
          </div>

          {/* Quick-bid chips — relative to current top (or suggested) */}
          <div className="offer-quick-bids">
            <button type="button" className="offer-quick-bid" onClick={() => handleQuickBid(5)} disabled={submitting}>
              {currentTop ? `+$5 over top` : `$${suggestedMin}`}
            </button>
            <button type="button" className="offer-quick-bid" onClick={() => handleQuickBid(10)} disabled={submitting}>
              {currentTop ? `+$10 over top` : `$${suggestedMin + 10}`}
            </button>
            <button type="button" className="offer-quick-bid" onClick={() => handleQuickBid(25)} disabled={submitting}>
              {currentTop ? `+$25 over top` : `$${suggestedMin + 25}`}
            </button>
            {askingPrice > 0 && (
              <button type="button" className="offer-quick-bid offer-quick-bid--asking" onClick={() => setAmount(String(askingPrice))} disabled={submitting}>
                Asking ${askingPrice}
              </button>
            )}
          </div>

          {/* Live indicator: are you leading? */}
          {offerAmount >= 10 && (
            <div className={`offer-status ${isLeading ? 'offer-status--leading' : 'offer-status--outbid'}`}>
              {isLeading
                ? (currentTop ? `✓ You'd lead by $${offerAmount - currentTop}` : '✓ First offer — you set the top')
                : `⚠ ${currentTop! - offerAmount + 1 > 0 ? `$${currentTop! - offerAmount + 1} below top — go higher to lead` : 'tied — go higher'}`}
            </div>
          )}

          {error && <span className="offer-error">{error}</span>}

          <label className="offer-label">Message (optional)</label>
          <textarea
            placeholder="Add a message to Jalen about your offer..."
            value={message}
            onChange={e => setMessage(e.target.value)}
            className="offer-message"
            rows={3}
            disabled={submitting}
            maxLength={500}
          />

          <div className="offer-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 16v-4M12 8h.01"/>
            </svg>
            <span>Jalen reviews offers personally — usually responds within 24h via DM.</span>
          </div>

          <div className="offer-expiry-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Offers expire in 7 days if not accepted.</span>
          </div>

          <button
            className="submit-offer-btn"
            onClick={handleSubmit}
            disabled={submitting || isBelowMin || isOutbid}
          >
            {submitting ? (
              <>
                <span className="offer-spinner" /> Sending…
              </>
            ) : (
              `Send Offer · $${offerAmount || 0}`
            )}
          </button>

          <button className="cancel-offer-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default OfferModal;
